<template>
  <v-app-bar app dark color="primary" elevation="0" clipped-left class="pl-3">
    <v-app-bar-nav-icon v-if="showSideNavigationButton">
      <template v-slot:default>
        <v-btn icon @click="toggleNavigation()">
          <v-icon>fas fa-bars</v-icon>
        </v-btn>
      </template>
    </v-app-bar-nav-icon>
    <v-toolbar-title>
      <router-link
        data-cy="appToolbar__homeLink"
        alt="Link to Home page"
        :to="{ path: '/' }"
        class="white--text och-homeLink"
      >
        <img class="och-logo" src="/ochin-valet-logo.png" alt="OCHIN logo" />
      </router-link>
    </v-toolbar-title>

    <template v-if="sessionOrganization && initialLoadComplete && isOchinUser">
      <v-dialog v-model="dialog" persistent max-width="700">
        <template v-slot:activator="{ on, attrs }">
          <v-btn text v-bind="attrs" v-on="on" elevation="0">
            <span data-cy="appToolbar__organization">{{
              sessionOrganization.name
            }}</span>
            <v-icon dark class="ml-3">fa fa-caret-down</v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-title class="headline">Select Service Area...</v-card-title>
          <v-card-text class="mt-2">
            <p>
              Select the service area you'd like to work in by selecting it from
              the dropdown below. Once the service area has been selected, your
              session will be updated and the browser will refresh.
            </p>
            <v-autocomplete
              @change="updateServiceArea"
              :hide-details="true"
              :item-text="
                item =>
                  `${item.name} SA ${item.serviceAreaId} ${item.organizationId}`
              "
              :items="organizationList"
              class="mx-5 mt-8"
              data-cy="appToolbar__organizationSelector"
              elevation="0"
              item-value="organizationId"
              label="Organization"
              name="organizationSelector"
              outlined
              v-model="sessionOrganization"
            >
              <template v-slot:item="{ item }">
                <v-chip
                  small
                  label
                  pill
                  class="mr-2 px-2"
                  v-if="item.serviceAreaId"
                >
                  SA {{ item.serviceAreaId }}
                </v-chip>
                {{ organizationNameFormatter(item) }}
              </template>
              <template v-slot:selection="{ item, parent }">
                <template v-if="!parent.internalSearch">
                  <v-chip
                    small
                    label
                    pill
                    class="mr-2 px-2"
                    v-if="item.serviceAreaId"
                  >
                    SA {{ item.serviceAreaId }}
                  </v-chip>
                  {{ organizationNameFormatter(item) }}
                </template>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-spacer></v-spacer>
    </template>

    <template v-else-if="organization && initialLoadComplete">
      <div class="title och-overflow-ellipsis">{{ organization.name }}</div>
      <v-spacer></v-spacer>
    </template>
    <div data-cy="appToolbar__userMenu--container">
      <v-menu offset-y v-if="loggedIn" data-cy="appToolbar__userMenu">
        <template v-slot:activator="{ on }">
          <v-btn
            large
            class="och-button--usermenu elevation-0"
            color="transparent"
            data-cy="appToolbar__userName"
            v-on="on"
          >
            <v-icon class="mr-2">fas fa-user-circle</v-icon>{{ user.displayName
            }}<v-icon dark class="ml-2">fa fa-caret-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-subheader>ACCOUNT INFO</v-subheader>
          <v-list-item-group>
            <v-list-item data-cy="appToolbar__accountInfo" inactive>
              <v-list-item-icon>
                <v-icon class="mr-5" color="accent">fas fa-user</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <div class="body-1 font-weight-bold">
                    {{ user.displayName }}
                  </div>
                  <div>{{ user.email }}</div>
                  <div
                    data-cy="appToolbar__crowdUsername"
                    v-if="user.email !== username"
                    class="caption"
                  >
                    (Username: {{ username }})
                  </div>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
          <v-divider></v-divider>
          <v-subheader>LINKS</v-subheader>
          <v-list-item-group data-cy="appToolbar__accountInfo">
            <v-list-item
              v-if="organizationAdmin"
              data-cy="appToolbar__adminLink"
              @click="$router.push('/ochin')"
              @keyenter="$router.push('/ochin')"
            >
              <v-list-item-icon>
                <v-icon class="mr-5" color="accent">fas fa-cog</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title> Admin </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>

          <v-list-item-group>
            <v-list-item
              data-cy="appToolbar__logoutLink"
              @click="logout"
              @keyenter="logout"
            >
              <v-list-item-icon>
                <v-icon class="mr-5" color="secondary"
                  >fas fa-sign-out-alt</v-icon
                >
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title> Logout </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
    </div>
  </v-app-bar>
</template>

<script>
import { cloneDeep } from "lodash";
import { mapActions, mapState, mapMutations } from "vuex";

export default {
  name: "TopNavBar",
  components: {},
  computed: {
    showSideNavigationButton() {
      return !this.$route.meta.disableSideNavigation;
    },
    ...mapState({
      isOchinUser: state => state.session.isOchinUser,
      loggedIn: state => state.session.loggedIn,
      organization: state => state.organization.record,
      organizationAdmin: state =>
        state.session.user.permission.organization.admin,
      organizationList: state => state.organization.listAdmin,
      user: state => state.session.user,
      userOrganizationId: state => state.session.organizationId,
      username: state => state.session.username,
    }),
  },
  async created() {
    await this.fetchSession();
    await this.fetchOrganization(this.userOrganizationId);
    this.sessionOrganization = cloneDeep(this.organization);
    if (this.isOchinUser) {
      await this.fetchOrganizationListAdmin();
    }
    this.initialLoadComplete = true;
  },
  data() {
    return {
      dialog: false,
      initialLoadComplete: false,
      sessionOrganization: null,
    };
  },
  methods: {
    logout() {
      this.removeCookies();
      document.location = "/";
    },
    organizationNameFormatter(organization) {
      const name = `${organization.name} (${organization.organizationId})`;

      return name;
    },
    async updateServiceArea(serviceAreaId) {
      try {
        await this.changeOrganization(serviceAreaId);
        document.location.reload();
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
        this.$notifyError({
          message: "Could not switch to selected organization",
        });
      }
    },
    ...mapActions({
      changeOrganization: "session/changeOrganization",
      fetchOrganization: "organization/getByOrganizationId",
      fetchOrganizationListAdmin: "organization/listAdmin",
      fetchSession: "session/loadSession",
      removeCookies: "session/logout",
      reset: "notification/reset",
    }),
    ...mapMutations({
      toggleNavigation: "application/setSideNavigation",
    }),
  },
};
</script>
<style>
.och-overflow-ellipsis {
  text-overflow: ellipsis;
  white-space: pre;
  overflow: hidden;
}
.v-app-bar--fixed {
  z-index: 199 !important;
}
</style>
